import React, { useMemo, useEffect } from "react";
import {
  Box,
  OutlinedInput,
  Button,
  Tooltip,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { CustomDropdown } from "../../orderStatus/statusList";
import { STATUS_LIST } from "../constants/orderConstants";
import DynamicSelect from "./dynamicSelect";
import { useLazyGetPartnerAndSubuserListQuery } from 'services/dfy-service';

/**
 * Search and filter controls for orders
 */
const SearchFilters = ({
  searchText,
  status,
  partnerId,
  subUserId,
  onSearchChange,
  onStatusChange,
  onPartnerChange,
  onSubUserChange,
  onExportAllCSV,
  isExporting,
}) => {
  // Lazy query to fetch all partners and their sub-users
  const [
    fetchPartnerAndSubuserList,
    { data: partnerSubuserData, isLoading: isLoadingPartnerData }
  ] = useLazyGetPartnerAndSubuserListQuery();

  // Fetch partners and sub-users on component mount
  useEffect(() => {
    fetchPartnerAndSubuserList();
  }, [fetchPartnerAndSubuserList]);

  // Transform partners data to options format
  const partnerOptions = useMemo(() => {
    const options = [{ value: "", label: "All Partners" }];
    if (partnerSubuserData?.data) {
      partnerSubuserData.data.forEach((item) => {
        const { partner } = item;
        const name = `${partner.name?.first || ''} ${partner.name?.last || ''}`.trim();
        options.push({
          value: partner._id,
          label: name || partner.email || partner._id,
        });
      });
    }
    return options;
  }, [partnerSubuserData]);

  // Transform sub-users data to options format based on selected partner
  const subUserOptions = useMemo(() => {
    const options = [{ value: "", label: "All Sub-Users" }];
    if (partnerId && partnerSubuserData?.data) {
      const selectedItem = partnerSubuserData.data.find(
        (item) => item.partner._id === partnerId
      );
      if (selectedItem?.subusers) {
        selectedItem.subusers
          .filter((user) => user.partnerId === partnerId)
          .forEach((user) => {
            const name = `${user.name?.first || ""} ${user.name?.last || ""}`.trim();
            options.push({
              value: user._id,
              label: name || user.email,
            });
          });
      }
    }
    return options;
  }, [partnerSubuserData, partnerId]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <OutlinedInput
          onChange={onSearchChange}
          size="small"
          placeholder="Search"
          value={searchText}
          aria-label="Search orders"
        />

        <CustomDropdown
          label={status}
          options={STATUS_LIST}
          selected={status}
          handleSelect={onStatusChange}
        />

        <DynamicSelect
          label={isLoadingPartnerData ? "Loading..." : "Select White Label Partner"}
          selectValue={partnerId}
          onSortChange={onPartnerChange}
          options={partnerOptions}
          minWidth={220}
        />
        <DynamicSelect
          label={isLoadingPartnerData ? "Loading..." : "Select Sub-User or All"}
          selectValue={subUserId}
          onSortChange={onSubUserChange}
          options={subUserOptions}
          minWidth={220}
          disabled={!partnerId || isLoadingPartnerData}
        />
      </Box>

      <Tooltip
        title={!partnerId ? "Please select a partner first to export data" : ""}
        arrow
        placement="top"
      >
        <span>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Download />}
            onClick={onExportAllCSV}
            disabled={isExporting || !partnerId}
          >
            {isExporting ? "Exporting..." : "Export The Data Including Bill Via Csv"}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default SearchFilters;